import { Component, type ReactNode } from 'react';
import { screenRect } from '../../helpers/utils/scrollFrame';

export const DEALER_HANDOFF_EVENT = 'spades:dealer-handoff-start';

const COUNT_MS = 700;
const PULSE_MS = 360;
const POST_PULSE_PAUSE_MS = 200;
/** The reset: team bids and the round number each turn over once, like a
 * block, in the slot the bids used to count down in. */
const FLIP_MS = 700;
const POST_BID_PAUSE_MS = 150;

type Props = { count: number; children: ReactNode };
type Copy = {
  source: HTMLElement;
  copy: HTMLElement;
  key: string;
  value: number;
};
const SCORE = '[data-testid="team1-score"], [data-testid="team2-score"]';
const BID =
  '[data-testid$="-bid-total"], .classic-bid-heading .classic-total-value';
const SEATS = '[data-deal-seat]';
const ROUND_LABEL = '[data-round-label]';
const BOARD = '[data-cy="table-round"], [data-cy="round"]:not([data-round])';

// These copies only paint scores, bid labels and seat cards. Freeze their
// layout and visible paint, rather than materializing the entire CSSOM (which
// includes hundreds of irrelevant animation, grid and inherited theme values).
const SNAPSHOT_PAINT = [
  'display',
  'position',
  'top',
  'right',
  'bottom',
  'left',
  'box-sizing',
  'width',
  'height',
  'min-width',
  'min-height',
  'max-width',
  'max-height',
  'margin',
  'padding',
  'gap',
  'row-gap',
  'column-gap',
  'flex',
  'flex-direction',
  'flex-wrap',
  'align-items',
  'align-self',
  'justify-content',
  'order',
  'overflow',
  'white-space',
  'text-overflow',
  'vertical-align',
  'font-family',
  'font-size',
  'font-weight',
  'font-style',
  'line-height',
  'letter-spacing',
  'text-align',
  'text-transform',
  'text-decoration',
  'color',
  'background',
  'border',
  'border-radius',
  'box-shadow',
  'text-shadow',
  'opacity',
  'visibility',
  'transform',
  'transform-origin',
  'zoom',
  'z-index',
  'fill',
  'stroke',
  'stroke-width',
  'stroke-linecap',
  'stroke-linejoin',
  'clip-path',
  'mask',
  'filter',
  'backdrop-filter',
];

/** Copy paint before the keyed round is replaced. Viewport coordinates (screen
 * px in every engine) avoid inheriting the stage's zoom twice. Copies are decorative and never interactive. */
function snapshot(source: HTMLElement): HTMLElement {
  const copy = source.cloneNode(true) as HTMLElement;
  const originals = [source, ...source.querySelectorAll<HTMLElement>('*')];
  const copies = [copy, ...copy.querySelectorAll<HTMLElement>('*')];
  originals.forEach((node, i) => {
    const paint = getComputedStyle(node);
    // Computed standard properties already resolve var(). Copying Chakra's
    // inherited theme variables onto every descendant adds thousands of
    // declarations and blocks the final-made dismissal. Assign once to avoid
    // thousands of individual style mutations.
    copies[i].style.cssText = SNAPSHOT_PAINT.map(
      (property) => `${property}:${paint.getPropertyValue(property)};`,
    ).join('');
    // Fractional widths can re-ellipsize a short name when its painted copy
    // is outside the zoomed stage. Preserve genuinely clipped long names.
    if (
      node.hasAttribute('data-seat-name') &&
      node.scrollWidth <= node.clientWidth + 1
    ) {
      copies[i].style.overflow = 'visible';
      copies[i].style.textOverflow = 'clip';
    }
    if (node.dataset.testid === 'dealerBadge')
      copies[i].style.visibility = 'hidden';
    for (const attr of Array.from(copies[i].attributes))
      if (/^(id|class|role|tabindex|title|aria-.+|data-.+)$/.test(attr.name))
        copies[i].removeAttribute(attr.name);
  });
  const rect = screenRect(source);
  const scale = source.offsetWidth ? rect.width / source.offsetWidth : 1;
  Object.assign(copy.style, {
    position: 'fixed',
    left: `${rect.left}px`,
    top: `${rect.top}px`,
    width: `${rect.width / scale}px`,
    height: `${rect.height / scale}px`,
    margin: '0',
    transform: `scale(${scale})`,
    transformOrigin: 'top left',
    zoom: '1',
    pointerEvents: 'none',
    zIndex: '10',
  });
  copy.setAttribute('aria-hidden', 'true');
  copy.inert = true;
  return copy;
}

/** "ROUND 1" as a copy whose number can turn over on its own while the word
 * stays put. It covers the label's text only: Classic draws rules either
 * side with ::before/::after, so the real label is hidden by colour, not
 * opacity, and its rules stay. */
function roundNumberCopy(label: HTMLElement): HTMLElement | null {
  const words = /^(.*?)(\d+)\s*$/.exec(label.textContent ?? '');
  if (!words) return null;
  const range = document.createRange();
  range.selectNodeContents(label);
  const box = screenRect(label);
  const text =
    typeof range.getBoundingClientRect === 'function' ? screenRect(range) : box;
  const scale = label.offsetWidth ? box.width / label.offsetWidth : 1;
  const paint = getComputedStyle(label);
  const copy = document.createElement('span');
  copy.style.cssText = [
    'font-family',
    'font-size',
    'font-weight',
    'font-style',
    'letter-spacing',
    'text-transform',
    'color',
    'text-shadow',
  ]
    .map((property) => `${property}:${paint.getPropertyValue(property)};`)
    .join('');
  Object.assign(copy.style, {
    position: 'fixed',
    left: `${text.left}px`,
    top: `${text.top}px`,
    margin: '0',
    // The text's own box: no leading, so the glyphs sit where they were.
    lineHeight: `${text.height / scale}px`,
    whiteSpace: 'nowrap',
    transform: `scale(${scale})`,
    transformOrigin: 'top left',
    pointerEvents: 'none',
    zIndex: '10',
  });
  const number = document.createElement('span');
  number.style.display = 'inline-block';
  number.textContent = words[2];
  copy.append(words[1], number);
  copy.setAttribute('aria-hidden', 'true');
  copy.inert = true;
  return copy;
}

/** Presentation only: scoring and the next round commit immediately.
 * Reuse the existing page root: FitStage measures its direct parent's width,
 * so inserting a wrapper (even display:contents) changes its fit calculation. */
export default class RoundCompletion extends Component<Props> {
  private stop?: () => void;
  private boardHeight = 0;
  private get root() {
    return document.querySelector<HTMLElement>('.App');
  }
  /** The current board's round label (history rows have their own). */
  private get roundLabel() {
    return Array.from(
      this.root?.querySelectorAll<HTMLElement>(ROUND_LABEL) ?? [],
    ).find((label) => !label.closest('[data-round]'));
  }

  getSnapshotBeforeUpdate(previous: Props): Copy[] | null {
    if (previous.count === this.props.count) return null;
    this.stop?.();
    if (
      this.props.count !== previous.count + 1 ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    )
      return null;
    const board = this.root?.querySelector<HTMLElement>(BOARD);
    this.boardHeight = board ? parseFloat(getComputedStyle(board).height) : 0;
    const copies: Copy[] = Array.from(
      this.root?.querySelectorAll<HTMLElement>(`${SCORE}, ${BID}, ${SEATS}`) ??
        [],
    )
      .filter((el) => !el.closest('[data-round]'))
      .map((source) => ({
        source,
        copy: snapshot(source),
        key: source.matches(SCORE)
          ? source.dataset.testid!
          : source.matches(BID)
            ? (source.dataset.testid ??
              `classic-${source.closest('[data-team]')?.getAttribute('data-team')}`)
            : '',
        value: Number(source.textContent),
      }));
    const label = this.roundLabel;
    const round = label && roundNumberCopy(label);
    if (label && round)
      copies.push({ source: label, copy: round, key: 'round', value: NaN });
    return copies;
  }

  componentDidUpdate(_previous: Props, _state: unknown, copies: Copy[] | null) {
    if (!copies?.length) return;
    const cleanups: (() => void)[] = [];
    const transitions: Animation[] = [];
    let collapse: Animation | undefined;
    const board = this.root?.querySelector<HTMLElement>(BOARD);
    if (board) {
      const height = parseFloat(getComputedStyle(board).height);
      if (this.boardHeight > height + 0.5) {
        // Reserve outgoing layout without animating the actual board content.
        const spacer = document.createElement('div');
        spacer.setAttribute('aria-hidden', 'true');
        spacer.style.height = `${this.boardHeight - height}px`;
        spacer.style.pointerEvents = 'none';
        board.appendChild(spacer);
        collapse = spacer.animate?.(
          [{ height: `${this.boardHeight - height}px` }, { height: '0px' }],
          { duration: 250, easing: 'ease-out', fill: 'both' },
        );
        collapse?.pause?.();
        cleanups.push(() => {
          collapse?.cancel();
          spacer.remove();
        });
      }
    }
    const counts: {
      copy: HTMLElement;
      target: HTMLElement;
      from: number;
      to: number;
      done: boolean;
    }[] = [];
    // Each flip turns `rotor` edge-on, swaps in the new text, then turns it
    // back flat, so the old and new values never show at once.
    const flips: {
      copy: HTMLElement;
      rotor: HTMLElement;
      to: string;
      turn: (degrees: number) => string;
      reveal: () => void;
      turned: boolean;
      done: boolean;
    }[] = [];
    const defer = (animation: Animation | undefined) => {
      if (!animation) return;
      animation.pause?.();
      transitions.push(animation);
      cleanups.push(() => animation.cancel());
    };
    // Read all destinations before inserting or restyling any floating copies.
    // Interleaving these reads with writes forces a full layout per copy.
    const destinations = copies.map(({ source, key }) => {
      const target = !key
        ? this.root?.querySelector<HTMLElement>(
            `[data-deal-seat="${source.getAttribute('data-deal-seat')}"]`,
          )
        : key === 'round'
          ? this.roundLabel
          : key.startsWith('classic-')
            ? this.root?.querySelector<HTMLElement>(
                `.classic-bid-heading [data-team="${key.slice(8)}"] .classic-total-value`,
              )
            : this.root?.querySelector<HTMLElement>(`[data-testid="${key}"]`);
      return { target, rect: target && screenRect(target) };
    });
    for (const [index, { copy, key, value }] of copies.entries()) {
      const { target, rect: destination } = destinations[index];
      document.body.appendChild(copy);
      cleanups.push(() => copy.remove());
      if (!key) {
        copy.dataset.roundDeparture = '';
        const dx = destination
          ? destination.left - parseFloat(copy.style.left)
          : 0;
        const dy = destination
          ? destination.top - parseFloat(copy.style.top)
          : 0;
        defer(
          copy.animate?.(
            [
              { opacity: 1, transform: copy.style.transform },
              {
                opacity: 0,
                transform: `translate(${dx}px, ${dy}px) ${copy.style.transform}`,
              },
            ],
            { duration: 450, easing: 'ease-out', fill: 'both' },
          ),
        );
        continue;
      }
      if (key === 'round') {
        // Only the number turns; the word stays flat beside it.
        const to = /(\d+)\s*$/.exec(target?.textContent ?? '')?.[1];
        if (!target || !to) {
          copy.remove();
          continue;
        }
        const color = target.style.color;
        target.style.color = 'transparent';
        const reveal = () => {
          target.style.color = color;
        };
        cleanups.push(reveal);
        copy.dataset.roundCount = 'round';
        const depth = 3 * parseFloat(copy.style.lineHeight);
        flips.push({
          copy,
          rotor: copy.lastElementChild as HTMLElement,
          to,
          turn: (degrees) => `perspective(${depth}px) rotateX(${degrees}deg)`,
          reveal,
          turned: false,
          done: false,
        });
        continue;
      }
      if (!target || !Number.isFinite(value)) {
        copy.remove();
        continue;
      }
      const score = target.matches(SCORE);
      const to = score ? Number(target.textContent) : NaN;
      if (score && !Number.isFinite(to)) {
        copy.remove();
        continue;
      }
      const opacity = target.style.opacity;
      target.style.opacity = '0';
      cleanups.push(() => {
        target.style.opacity = opacity;
      });
      if (!destination) continue;
      const scale =
        Number(copy.style.transform.match(/scale\(([^)]+)\)/)?.[1]) || 1;
      const width = Math.max(
        parseFloat(copy.style.width),
        destination.width / scale,
      );
      copy.style.width = `${width}px`;
      copy.style.left = `${destination.left + destination.width / 2 - (width * scale) / 2}px`;
      copy.style.textAlign = 'center';
      copy.style.whiteSpace = 'nowrap';
      copy.style.opacity = '1';
      copy.textContent = String(value);
      copy.dataset.roundCount = score ? 'score' : 'bid';
      if (score) {
        counts.push({ copy, target, from: value, to, done: false });
        continue;
      }
      // The whole team bid turns about its centre, inside the copy's zoom.
      const base = copy.style.transform;
      const x = width / 2;
      const y = parseFloat(copy.style.height) / 2;
      flips.push({
        copy,
        rotor: copy,
        to: target.textContent ?? '',
        turn: (degrees) =>
          `${base} translate(${x}px, ${y}px) perspective(${6 * y}px) rotateX(${degrees}deg) translate(${-x}px, ${-y}px)`,
        reveal: () => {
          target.style.opacity = '';
        },
        turned: false,
        done: false,
      });
    }
    this.root?.querySelectorAll<HTMLElement>(SEATS).forEach((seat) => {
      if (seat.closest('[data-round]')) return;
      defer(
        seat.animate?.([{ opacity: 0 }, { opacity: 1 }], {
          duration: 550,
          easing: 'ease-in-out',
          fill: 'both',
        }),
      );
    });
    const count = this.props.count;
    const releaseDealer = () =>
      window.dispatchEvent(
        new CustomEvent(DEALER_HANDOFF_EVENT, { detail: count }),
      );
    let start: number | undefined;
    let resetAt: number | undefined;
    let resetting = false;
    let collapsing = false;
    let dealerAt: number | undefined;
    let dealerReleased = false;
    let frame = 0;
    const tick = (now: number) => {
      start ??= now;
      const elapsed = now - start;
      if (elapsed >= COUNT_MS && resetAt === undefined)
        resetAt = elapsed + PULSE_MS + POST_PULSE_PAUSE_MS;
      if (resetAt !== undefined && elapsed >= resetAt && !resetting) {
        resetting = true;
        transitions.forEach((animation) => animation.play?.());
      }
      if (resetAt !== undefined && elapsed >= resetAt + 450 && !collapsing) {
        collapsing = true;
        collapse?.play?.();
      }
      for (const item of counts) {
        if (item.done) continue;
        const progress = Math.min(1, elapsed / COUNT_MS);
        item.copy.textContent = String(
          Math.round(item.from + (item.to - item.from) * progress),
        );
        if (progress < 1) continue;
        item.done = true;
        item.copy.remove();
        item.target.style.opacity = '';
        const pulse = item.target.animate?.(
          [
            { transform: 'scale(1)' },
            {
              transform: 'scale(1.15)',
              offset: 0.4,
            },
            { transform: 'scale(1)' },
          ],
          { duration: PULSE_MS, easing: 'ease-out' },
        );
        cleanups.push(() => pulse?.cancel());
      }
      for (const flip of flips) {
        if (flip.done) continue;
        const progress =
          resetAt === undefined
            ? 0
            : Math.max(0, Math.min(1, (elapsed - resetAt) / FLIP_MS));
        // Speeds up to edge-on, then settles: 0° → 90°, swap, −90° → 0°.
        const half = progress < 0.5 ? progress * 2 : progress * 2 - 1;
        const degrees =
          progress < 0.5 ? 90 * half * half : -90 * (1 - half) * (1 - half);
        flip.rotor.style.transform = flip.turn(degrees);
        if (progress >= 0.5 && !flip.turned) {
          flip.turned = true;
          flip.rotor.textContent = flip.to;
        }
        if (progress < 1) continue;
        flip.done = true;
        flip.copy.remove();
        flip.reveal();
      }
      if (
        resetAt !== undefined &&
        elapsed >= resetAt + FLIP_MS &&
        dealerAt === undefined
      )
        dealerAt = elapsed + POST_BID_PAUSE_MS;
      if (dealerAt !== undefined && elapsed >= dealerAt) {
        dealerReleased = true;
        releaseDealer();
        this.stop?.();
      } else frame = requestAnimationFrame(tick);
    };
    const displaced = () => this.stop?.();
    window.addEventListener('resize', displaced);
    window.addEventListener('scroll', displaced, true);
    frame = requestAnimationFrame(tick);
    this.stop = () => {
      if (!dealerReleased) releaseDealer();
      window.removeEventListener('resize', displaced);
      window.removeEventListener('scroll', displaced, true);
      cancelAnimationFrame(frame);
      cleanups.forEach((cleanup) => cleanup());
      this.stop = undefined;
    };
  }
  componentWillUnmount() {
    this.stop?.();
  }
  render() {
    return this.props.children;
  }
}
